import React, { useContext, useState } from "react";
import { Avatar, Badge, Dropdown, Modal, Space, Tag, Tooltip } from "antd";
import {
  CalendarOutlined,
  CommentOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
  PaperClipOutlined,
  UserOutlined,
  CheckSquareOutlined,
  FlagOutlined,
  SettingOutlined,
  FolderOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { TTaskWithWorkInfo } from "../../types/dashboard.types";
import styles from "./task-card.module.css";
import { formatDistanceToNow } from "date-fns";
import CustomDrawer from "../drawer";
import { ePriority, ePrivileges } from "../../utils/enum";
import DateUtils from "../../utils/date";
import TaskDetailsModal from "./task-details";
import { TDropdownOptions } from "../../types/config";
import Utils from "../../utils";
import { UserContext } from "../../context";
import AddEditTaskForm from "../../pages/dashboard/dashboard/components/add-edit-task/task-form";
import AddEditWorkForm from "../../pages/dashboard/dashboard/components/add-edit-work/work-form";
import WorkDetailsModal from "../../pages/dashboard/dashboard/components/work-details-modal/work-details";

interface TaskCardProps {
  task: TTaskWithWorkInfo;
  onDragStart: (
    e: React.DragEvent<HTMLDivElement>,
    task: TTaskWithWorkInfo
  ) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrag?: (e: React.DragEvent<HTMLDivElement>) => void;
  statusOptions: TDropdownOptions[];
  statusId: number;
  projectId: number;
  projectCategoryId?: string;
  color: string;
  fetchSpecificProjectDashboardData: (id: string) => void;
  setNewTaskDetails: (data: any) => void;
  updateTask: (data: any) => void;
  workId: number;
  workCategoryName?: string;
  workTitle?: string;
  onStatusChange?: (taskId: number, newStatusId: number) => void;
  onAssigneeChange?: (taskId: number, assigneeId: number) => void;
}

const getPriorityColor = (priority: number): string => {
  switch (priority) {
    case 3:
      return "var(--priority-high)"; // High - Burgundy pink
    case 2:
      return "var(--priority-moderate)"; // Medium - Main burgundy
    case 1:
      return "var(--priority-low)"; // Low - Dark burgundy
    default:
      return "var(--text-secondary)"; // Default - Gray
  }
};

const getPriorityIcon = (priority: number) => {
  switch (priority) {
    case 3:
      return <FlagOutlined />;
    case 2:
      return <FlagOutlined />;
    case 1:
      return <FlagOutlined />;
    default:
      return <FlagOutlined />;
  }
};

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  onDragStart,
  onDragEnd,
  onDrag,
  statusId,
  color,
  projectId,
  projectCategoryId,
  statusOptions,
  workId,
  fetchSpecificProjectDashboardData,
  setNewTaskDetails,
  updateTask,
  workCategoryName,
  workTitle,
  onStatusChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [isWorkModalOpen, setIsWorkModalOpen] = useState(false);
  const context: any = useContext(UserContext);

  const timeLeft = task.endDate
    ? formatDistanceToNow(new Date(task.endDate), { addSuffix: true })
    : null;

  const isOverdue = task.endDate && new Date(task.endDate) < new Date();

  const handleViewTaskDetails = (id: number) => {
    setSelectedTaskId(id);
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  console.log(task);

  const getInitials = (name: string): string => {
    return name
      ?.split(" ")
      ?.map((word) => word.charAt(0))
      ?.join("")
      ?.toUpperCase()
      ?.slice(0, 2);
  };

  return (
    <>
      <div
        className={styles.taskCard}
        draggable
        onDragStart={(e) => onDragStart(e, task)}
        onDragEnd={onDragEnd}
        onDrag={onDrag}
        data-status={statusId}
        tabIndex={0}
        role='button'
        aria-label={`Task: ${task.taskCategoryName}`}
      >
        {/* Card Header */}
        <div className={styles.cardHeader}>
          <div className={styles.titleSection}>
            <Tooltip
              title={task.taskCategoryName}
              placement='top'
            >
              <h4 className={styles.taskTitle}>{task.taskCategoryName}</h4>
            </Tooltip>
            <Tag
              color={getPriorityColor(task.priority)}
              className={styles.priorityTag}
              icon={getPriorityIcon(task.priority)}
            >
              {ePriority[task.priority]}
            </Tag>
          </div>
        </div>

        {/* Card Body - Scrollable content */}
        <div className={styles.cardBody}>
          {/* Ticket Number Badge */}
          {task.ticketNumber && (
            <div className={styles.ticketRow}>
              <span className={styles.ticketNumber}>{task.ticketNumber}</span>
            </div>
          )}

          {/* Work Info */}
          <div className={styles.workInfo}>
            <span className={styles.categoryBadge}>{workCategoryName}</span>
            <Tooltip
              title={workTitle}
              placement='top'
            >
              <span className={styles.workTitle}>
                {workTitle && workTitle.length > 30
                  ? `${workTitle.slice(0, 30)}...`
                  : workTitle}
              </span>
            </Tooltip>
          </div>

          {/* Meta Information Row */}
          <div className={styles.metaRow}>
            <div className={styles.assigneeInfo}>
              {task.assignedUser && task.assignedUser.length > 0 ? (
                <>
                  <Avatar.Group
                    maxCount={2}
                    size='small'
                  >
                    {task.assignedUser.map((user) => (
                      <Tooltip
                        key={user.id}
                        title={user.fullName}
                        placement='top'
                      >
                        <Avatar
                          size='small'
                          style={{ backgroundColor: color }}
                        >
                          {getInitials(user.fullName)}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </Avatar.Group>
                  <Tooltip
                    title={
                      <div>
                        {task.assignedUser.map((user) => (
                          <div key={user.id}>{user.fullName}</div>
                        ))}
                      </div>
                    }
                    placement='top'
                  >
                    <span className={styles.assigneeName}>
                      {task.assignedUser.length === 1
                        ? task.assignedUser[0].fullName
                        : `${task.assignedUser.length} members`}
                    </span>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Avatar
                    size='small'
                    icon={<UserOutlined />}
                  />
                  <span className={styles.assigneeName}>Unassigned</span>
                </>
              )}
            </div>
            {timeLeft && (
              <div
                className={`${styles.timeLeft} ${isOverdue ? styles.overdue : styles.upcoming}`}
              >
                {!isOverdue && <ClockCircleOutlined className={styles.icon} />}
                {timeLeft}
              </div>
            )}
          </div>

          {/* Dates and Time */}
          {task.startDate && task.endDate && (
            <div className={styles.dateRow}>
              <CalendarOutlined className={styles.icon} />
              <span className={styles.dates}>
                {DateUtils.format(task.startDate).slice(0, 11)} -{" "}
                {DateUtils.format(task.endDate).slice(0, 11)}
              </span>
            </div>
          )}

          {(task.estimatedTime || task.actualTime) && (
            <div className={styles.timeRow}>
              {task.estimatedTime && (
                <span className={styles.timeChip}>
                  <FieldTimeOutlined className={styles.icon} /> {task.estimatedTime}m
                </span>
              )}
              {task.actualTime && (
                <span className={styles.timeChip}>
                  <CheckCircleOutlined className={styles.icon} /> {task.actualTime}m
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer with counts and actions */}
        <div className={styles.cardFooter}>
          <div className={styles.counts}>
            <Space size={4}>
              {(task.commentsCount || 0) > 0 && (
                <span className={styles.countBadge}>
                  <CommentOutlined /> {task.commentsCount}
                </span>
              )}
              {(task.subtasksCount || 0) > 0 && (
                <span className={styles.countBadge}>
                  <CheckSquareOutlined /> {task.subtasksCount}
                </span>
              )}
              {(task.attachmentsCount || 0) > 0 && (
                <span className={styles.countBadge}>
                  <PaperClipOutlined /> {task.attachmentsCount}
                </span>
              )}
            </Space>
          </div>

          <div className={styles.footerActions}>
            <Space size={6}>
              <Tooltip title='View Details'>
                <EyeOutlined
                  className={styles.actionIcon}
                  onClick={() => handleViewTaskDetails(task.taskCategoryId)}
                />
              </Tooltip>
              <Tooltip title='View Work'>
                <FolderOutlined
                  className={styles.actionIcon}
                  onClick={() => setIsWorkModalOpen(true)}
                />
              </Tooltip>
              {Utils.isUserHasAccess(
                context?.user?.privilegeList,
                ePrivileges.ADDTASK
              ) && (
                <CustomDrawer
                  size='large'
                  trigger={
                    <Tooltip title='Edit Task'>
                      <EditOutlined className={styles.actionIcon} />
                    </Tooltip>
                  }
                  component={AddEditTaskForm}
                  componentProps={{
                    id: task.taskCategoryId,
                    projectId: projectId.toString(),
                    setNewTaskDetails: setNewTaskDetails,
                    projectWorkMappingId: workId,
                    onRefreshList: (newTaskData: any) => {
                      if (projectId && newTaskData) {
                        updateTask(newTaskData);
                      }
                    },
                  }}
                  title='Edit Task'
                />
              )}
              {Utils.isUserHasAccess(
                context?.user?.privilegeList,
                ePrivileges.ADDWORK
              ) && (
                <CustomDrawer
                  size='large'
                  trigger={
                    <Tooltip title='Edit Work'>
                      <SettingOutlined className={styles.actionIcon} />
                    </Tooltip>
                  }
                  component={AddEditWorkForm}
                  componentProps={{
                    id: workId,
                    projectId: projectId.toString(),
                    projectCategoryId: projectCategoryId,
                    onRefreshList: () => {
                      if (projectId) {
                        fetchSpecificProjectDashboardData(projectId.toString());
                      }
                    },
                  }}
                  title='Edit Work'
                />
              )}
            </Space>
          </div>
        </div>
      </div>

      {/* Task Details Modal */}
      <Modal
        title={`${task.ticketNumber || `T-${task.taskCategoryId}`} - ${task.taskCategoryName}`}
        open={isModalOpen}
        onOk={() => {}}
        onCancel={handleModalCancel}
        footer={null}
        width='70%'
      >
        {selectedTaskId ? (
          <TaskDetailsModal
            id={selectedTaskId}
            fetchSpecificProjectDashboardData={
              fetchSpecificProjectDashboardData
            }
            selectedProject={projectId.toString()}
            statusOptions={statusOptions}
          />
        ) : (
          <p>No task selected.</p>
        )}
      </Modal>

      {/* Work Details Modal */}
      <Modal
        title='Work Details'
        open={isWorkModalOpen}
        onOk={() => {}}
        onCancel={() => setIsWorkModalOpen(false)}
        footer={null}
        width='70%'
      >
        <WorkDetailsModal workId={workId} />
      </Modal>
    </>
  );
};

// Wrap with React.memo to prevent unnecessary re-renders
export default React.memo(TaskCard);
